import {
  QuestionAttempt,
  Questionnaire,
  QuestionResponseNested,
} from '@letrustech/letrus-api-interfaces/dist/interfaces/letrus_api';
import PageLoading from 'components/PageLoading';
import SatisfactionRatingForm from 'components/SatisfactionRatingForm';
import React, {useEffect, useMemo} from 'react';
import {connect} from 'react-redux';
import {useLocation, useNavigate, useParams} from 'react-router-dom';
import {
  fetchLearningPathByIdRequest,
  getLearningPath,
  viewLearningPathCSATRequest,
} from 'store/reducers/learningPath';
import {
  fetchQuestionnaireByIdRequest,
  getQuestionnaire,
  getQuestionnaireStatus,
  postQuestionAnswerRequest,
} from 'store/reducers/questionnaires';

import {ApplicationState} from 'store/rootReducer';
import isEmpty from 'utils/functions/isEmpty';
import {useMobile} from 'utils/hooks';
import {LearningPathTemp} from 'utils/types/learningPath';
import {StoreStatus} from 'utils/types/store';
import {getUserData} from 'utils/functions/getUserData/getUserData';
import {useModals} from 'letrus-ui';
import styles from './SatisfactionRating.module.scss';
import StoreRatingModal from './StoreRatingModal';

// Questionnaire id hardcoded until information is available at learningPathUserInstance.
export const LEARNING_PATH_CSAT_ID = 145;

export interface SatisfactionReasons {
  questionId: string;
  choices: number[];
}

export interface SubmitParams {
  comment?: string;
  rating: string;
  satisfactionReasons?: SatisfactionReasons;
}

interface DispatchProps {
  postQuestionAttemptRequest: typeof postQuestionAnswerRequest;
  fetchQuestionnaireByIdRequest: typeof fetchQuestionnaireByIdRequest;
  fetchLearningPathByIdRequest: typeof fetchLearningPathByIdRequest;
  viewLearningPathOpinionRequest: typeof viewLearningPathCSATRequest;
}

interface StateProps {
  questionnaire: Questionnaire;
  questionnaireStatus: StoreStatus;
  learningPath?: LearningPathTemp;
}

type Props = DispatchProps & StateProps;

const SatisfactionRating: React.FC<Props> = ({
  questionnaire,
  questionnaireStatus,
  learningPath,
  postQuestionAttemptRequest,
  fetchQuestionnaireByIdRequest,
  fetchLearningPathByIdRequest,
  viewLearningPathOpinionRequest,
}) => {
  const modals = useModals(['storeRatingModal']);

  const navigate = useNavigate();
  const location = useLocation();
  const {learning_path_id} = useParams();
  const {isPWA, isAndroid, isIos} = useMobile();
  const {user} = getUserData();
  const isFocusModeComplete = Boolean(
    (location.state as {focusModeComplete?: boolean} | undefined)
      ?.focusModeComplete,
  );

  const availableRatingChoices = useMemo(
    () =>
      questionnaire?.steps?.filter(
        (step) => step?.question.question_type === 'checkbox',
      ),
    [questionnaire],
  );

  const lowerRatingChoices = useMemo(
    () => availableRatingChoices?.[0],
    [availableRatingChoices],
  );

  const higherRatingChoices = useMemo(
    () => availableRatingChoices?.[availableRatingChoices.length - 1],
    [availableRatingChoices],
  );

  function handleStoreRatingModalClose() {
    handleOnExit();
    modals.storeRatingModal?.close();
  }

  function getStoreLink(): string {
    if (isPWA) {
      if (isAndroid) {
        return 'https://play.google.com/store/apps/details?id=br.com.letrus.aluno.twa';
      }

      if (isIos) {
        return 'https://apps.apple.com/br/app/letrus-estudante/id1663276644';
      }
    }

    return '';
  }

  const handleSubmit = ({
    comment,
    rating,
    satisfactionReasons,
  }: SubmitParams) => {
    const newData: Partial<QuestionAttempt> = {
      step: undefined,
      user: user.id,
      source: 'web',
      answer: undefined,
      question: 0,
      responses: undefined,
      learning_path_user_instance: Number(learning_path_id),
    };

    if (isPWA && Number(rating) > 3 && (isIos || isAndroid)) {
      modals.storeRatingModal?.showModal();
    }

    const {steps} = questionnaire;

    steps?.forEach((step) => {
      if (step.question.question_type === 'rating') {
        postQuestionAttemptRequest(
          {
            ...newData,
            question: step.question.id,
            answer: rating,
          },
          {questionnaireId: LEARNING_PATH_CSAT_ID},
        );
      }

      if (step.question.question_type === 'text' && comment) {
        postQuestionAttemptRequest(
          {
            ...newData,
            question: step.question.id,
            answer: comment,
          },
          {questinnaireId: LEARNING_PATH_CSAT_ID},
        );
      }

      if (
        step.question.question_type === 'checkbox' &&
        step.question.id === Number(satisfactionReasons?.questionId) &&
        satisfactionReasons &&
        satisfactionReasons.choices.length >= 1
      ) {
        postQuestionAttemptRequest(
          {
            ...newData,
            question: Number(satisfactionReasons?.questionId),
            responses: satisfactionReasons?.choices.map<QuestionResponseNested>(
              (reason) => ({
                option: reason,
                feedback: '',
                id: -1,
                is_response_correct: false,
              }),
            ),
          },
          {questionnaireId: LEARNING_PATH_CSAT_ID},
        );
      }
    });
    if (!isPWA) {
      handleOnExit();
    }
  };

  const handleOnExit = () => {
    if (isFocusModeComplete) {
      window.location.replace('/');
      return;
    }

    navigate(`/`);
  };

  useEffect(() => {
    fetchQuestionnaireByIdRequest(LEARNING_PATH_CSAT_ID);
    fetchLearningPathByIdRequest(Number(learning_path_id));
  }, []);

  useEffect(() => {
    if (learningPath?.id) {
      viewLearningPathOpinionRequest(
        Number(learning_path_id),
        learningPath.step_number,
        learningPath.started,
      );
    }
  }, [learningPath]);

  return questionnaireStatus.loading || isEmpty(questionnaire) ? (
    <PageLoading />
  ) : (
    <>
      <div className={styles.wrapper}>
        <span className={styles.content}>
          <SatisfactionRatingForm
            onSubmit={handleSubmit}
            onExit={handleOnExit}
            lowerRatingChoices={lowerRatingChoices}
            higherRatingChoices={higherRatingChoices}
          />
        </span>
      </div>
      <StoreRatingModal
        onClose={handleStoreRatingModalClose}
        storeLink={getStoreLink()}
      />
    </>
  );
};

export default connect(
  (state: ApplicationState) => ({
    questionnaire: getQuestionnaire(state),
    questionnaireStatus: getQuestionnaireStatus(state),
    learningPath: getLearningPath(state),
  }),
  {
    postQuestionAttemptRequest: postQuestionAnswerRequest,
    fetchQuestionnaireByIdRequest,
    fetchLearningPathByIdRequest,
    viewLearningPathOpinionRequest: viewLearningPathCSATRequest,
  },
)(SatisfactionRating);
